import {classList} from 'nuffjs';
import {actionMixin, actionConf} from './action';

/**
 * @property {string} toggledClass=toggled class name used on the element to signal the toggle
 * status
 * @memberof Toggler
 * @extends Action.conf
 */
const conf = Object.assign({}, actionConf, {
	toggledClass: 'toggled',
});

/**
 * create the Toggler class with a dynamic super class
 *
 * @mixin
 * @param {typeof HTMLElement} Base the base class
 * @returns {typeof Toggler}
 */
const togglerMixin = (Base) =>
	/**
	 * @classdesc a Toggler is an Action with the command set to 'toggle' that alternates between
	 * two states. It is used for example to control [Collapsable]{@link Collapsable} elements or
	 * any other elements that have a `toggle()` method.
	 *
	 * @class Toggler
	 * @extends Action
	 *
	 * @version 1.0.0
	 * @path
	 *
	 */
	class extends actionMixin(Base) {
		static conf = conf;

		/**
		 * Default options, overridable by data-attributes
		 *
		 * @property {string} command=toggle the method to call on the target(s)
		 * @memberof Toggler
		 * @extends Action.defaultOpts
		 *
		 * @static
		 */
		static defaultOpts = Object.assign({}, super.defaultOpts, {
			command: 'toggle',
		});

		/**
		 * Initialization
		 *
		 * @memberof Toggler
		 * @instance
		 */
		firstConnectedCallback() {
			super.firstConnectedCallback();
			// initialize the current status by checking the class
			this.toggled = classList(this).contains(conf.toggledClass);
		}

		/**
		 * Internal method to execute the command
		 * It also update the internal toggled status.
		 *
		 * @param {Element[]} targets the list of targets, typically only one
		 * @param {string} command the method name to be called
		 * @param {array} params the parameters for the method call
		 * @returns {Promise} resolves immediately when the option waitForTargets is false,
		 * otherwise when all the target(s) commands finish their execution
		 *
		 * @memberof Toggler
		 * @instance
		 * @protected
		 * @ignore
		 *
		 */
		executing(targets, command, params) {
			this.resetToggled(!this.toggled);
			return super.executing(targets, command, params);
		}

		/**
		 * Same as calling {@link Toggler#execute} without parameters
		 *
		 * @memberof Toggler
		 * @instance
		 */
		toggle() {
			return this.execute();
		}

		/**
		 * check if it is toggled
		 *
		 * @returns {boolean}
		 * @memberof Toggler
		 * @instance
		 */
		isToggled() {
			return this.toggled;
		}

		/**
		 * Internal method to update the internal state
		 *
		 * Toggle the class accordingly and if the aria-expanded is present (used for collapsable
		 * patterns, see {@link https://adrianroselli.com/2020/05/disclosure-widgets.html}), update
		 * also the atttribute value accordingly
		 *
		 * @param {boolean} value
		 * @memberof Toggler
		 * @instance
		 * @protected
		 * @ignore
		 *
		 */
		resetToggled(value) {
			const changed = value !== this.toggled;
			if (changed) {
				const expanded = this.getAttribute('aria-expanded');
				this.toggled = value;
				classList(this).toggle(conf.toggledClass, this.toggled);
				if (expanded) {
					this.setAttribute('aria-expanded', expanded === 'true' ? 'false' : 'true');
				}
			}
		}
	};

export {togglerMixin, conf as togglerConf};
